import type {EditorState, Extension} from '@codemirror/state'
import {EditorView} from '@codemirror/view'
import type {Nodes} from 'mdast'

import {markdownLink} from '../../language-tools/markdown/builders'
import {inlineFormattingNodeTypes, type LinkParent, linkParentNodeTypes} from '../../language-tools/markdown/node-types'
import {
  getNodeAtOffset,
  getNodesContainingRange,
  getNodesInsideRange,
} from '../../language-tools/syntax-tree/node-range-utils'
import {isUrl} from '../../utils'
import {hasNonEmptySelection} from '../utils'
import {getMarkdownTree} from './markdown-parser'
import {isShiftPressed, shiftPressedExtension} from './shift-pressed'

/** Check if this node is allowed to contain a `Link` node. */
function isLinkParent(node: Nodes): node is LinkParent {
  return linkParentNodeTypes.has(node.type)
}

/** Check if this node is inline formatting content. */
function isInlineFormattingNode(node: Nodes): boolean {
  return inlineFormattingNodeTypes.has(node.type)
}

/**
 * If this is a URL paste and the selected text is a valid place to create a link, transforms the pasted text into a
 * Markdown hyperlink.
 */
function transformPastedUrlToLink(pastedText: string, state: EditorState) {
  if (isShiftPressed(state)) return pastedText
  if (!hasNonEmptySelection(state)) return pastedText
  if (!pastedText || !isUrl(pastedText)) return pastedText

  const selection = state.selection
  const {from, to} = selection.main
  const tree = getMarkdownTree(state)
  const fromNode = getNodeAtOffset(tree, from)
  const toNode = getNodeAtOffset(tree, to)

  if (!fromNode || !toNode) return pastedText
  if (fromNode === toNode && fromNode.type === 'link') return pastedText

  // Creating a link across nodes can create invalid Markdown so when crossing node boundaries,
  // require selection to start/end at node boundaries or within paragraph nodes
  // and ensure all nodes are inline formatting (not block-level like code blocks or lists)
  if (fromNode !== toNode) {
    const validStart = from === fromNode.position?.start.offset || fromNode.type === 'paragraph'
    const validEnd = to === toNode.position?.end.offset || toNode.type === 'paragraph'
    const allNodesInline = getNodesInsideRange(tree, from, to).every(isInlineFormattingNode)

    if (!validStart || !validEnd || !allNodesInline) return pastedText
  }

  // parent will be undefined if the whole document is selected, in which case we use the root (tree) as the parent
  const parent =
    getNodesContainingRange(tree, from, to, false).findLast(n => n.type !== 'text' && n.type !== 'delimiter') ?? tree

  // Links are only allowed inside of certain node types. This ensures we don't create a link in a code block, for example
  if (!isLinkParent(parent)) return pastedText

  // Using `serializeMarkdown` on a link node is smarter than just doing `"[text](url)"` ourselves. For example, it will
  // automatically escape brackets in the link text and URL
  return markdownLink(state.sliceDoc(from, to), pastedText)
}

const urlClipboardFilter = EditorView.clipboardInputFilter.of(transformPastedUrlToLink)

/**
 * Creates Markdown links when URLs are pasted onto selected text.
 */
export const pasteLinksExtension: Extension = [shiftPressedExtension, urlClipboardFilter]
